/**
 * Phase 27.38 — Knowledge Base page.
 *
 * A Netflix-style grid of KR-curated learning resources. Each card shows:
 *   • 16:9 cover art (uploaded custom cover OR auto-extracted YouTube
 *     thumbnail OR fallback icon for Google Drive links).
 *   • Title + description.
 *   • Category pills.
 *
 * Clicking a card opens the underlying URL in a new tab (YouTube video
 * or Google Drive document). KR admins get "New Resource" + edit/delete
 * actions on each card. Everyone else is read-only.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  BookOpen, Search, Plus, Play, FileText as FileTextIcon, ExternalLink,
  Edit3, Trash2, Filter, X, Loader2, Upload, Image as ImageIcon,
  Link as LinkIcon, Presentation, Sheet as SheetIcon, FileType2,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { PageHeader } from '@/components/layout/PageHeader';
import { useAuth } from '@/context/AuthContext';
import { isKRRole, getErrorMessage } from '@/lib/utils';
import { knowledgeAPI } from '@/lib/api';

const RESOURCE_TYPE_META = {
  video: { icon: Play, label: 'Video', tone: 'bg-rose-100 text-rose-800 border-rose-200' },
  document: { icon: FileTextIcon, label: 'Document', tone: 'bg-sky-100 text-sky-800 border-sky-200' },
};

// Attachment kinds — used both for the icon shown in the chip and for
// filtering in the admin form dropdown. Kept small on purpose so the
// admin doesn't have to think about taxonomy.
const ATTACHMENT_KIND_META = {
  link: { icon: LinkIcon, label: 'Link', tone: 'bg-slate-100 text-slate-700 border-slate-200' },
  doc: { icon: FileTextIcon, label: 'Document', tone: 'bg-sky-100 text-sky-800 border-sky-200' },
  slide: { icon: Presentation, label: 'Slides', tone: 'bg-amber-100 text-amber-800 border-amber-200' },
  pdf: { icon: FileType2, label: 'PDF', tone: 'bg-rose-100 text-rose-800 border-rose-200' },
  sheet: { icon: SheetIcon, label: 'Sheet', tone: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  other: { icon: LinkIcon, label: 'Other', tone: 'bg-slate-100 text-slate-700 border-slate-200' },
};

// Auto-guess an attachment kind from its URL so admins don't have to
// pick manually when the URL is obvious (Google Slides, PDF, etc.).
function guessAttachmentKind(url = '') {
  const u = url.toLowerCase();
  if (u.endsWith('.pdf') || u.includes('/pdf/')) return 'pdf';
  if (u.includes('slides.google.com') || u.includes('/presentation/')) return 'slide';
  if (u.includes('sheets.google.com') || u.includes('/spreadsheets/')) return 'sheet';
  if (u.includes('docs.google.com') || u.includes('/document/')) return 'doc';
  return 'link';
}

const EMPTY_FORM = {
  title: '',
  description: '',
  url: '',
  resource_type: 'video',
  categories: [],
  is_active: true,
  attachments: [],
  notify_agents_on_publish: true,
  is_urgent: false,
};

function ResourceCover({ resource }) {
  const [errored, setErrored] = useState(false);
  const url = resource?.cover_url;
  if (!url || errored) {
    // Fallback tile with a big icon — used when a document has no
    // custom cover or when a YouTube thumbnail 404s.
    const IconEl = resource?.resource_type === 'video' ? Play : FileTextIcon;
    return (
      <div className="aspect-video w-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
        <IconEl className="w-12 h-12 text-slate-400" />
      </div>
    );
  }
  return (
    <div className="aspect-video w-full relative overflow-hidden bg-slate-100">
      <img
        src={url}
        alt={resource?.title || 'Cover'}
        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        onError={() => setErrored(true)}
      />
      {resource?.resource_type === 'video' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors">
          <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg opacity-90 group-hover:scale-110 transition-transform">
            <Play className="w-6 h-6 text-slate-900 ml-1" fill="currentColor" />
          </div>
        </div>
      )}
    </div>
  );
}

function ResourceCard({ resource, isKR, onEdit, onDelete }) {
  const meta = RESOURCE_TYPE_META[resource.resource_type] || RESOURCE_TYPE_META.document;
  const openLink = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!resource.url) return;
    window.open(resource.url, '_blank', 'noopener,noreferrer');
  };
  // "NEW" badge for anything created in the last 7 days so agents can
  // spot fresh trainings at a glance.
  const isNew = (() => {
    if (!resource.created_at) return false;
    try {
      const created = new Date(resource.created_at).getTime();
      return Date.now() - created < 7 * 24 * 60 * 60 * 1000;
    } catch { return false; }
  })();
  return (
    <Card
      className="group overflow-hidden hover:shadow-lg transition-shadow border-slate-200 relative flex flex-col"
      data-testid={`knowledge-card-${resource.resource_id}`}
    >
      {(isNew || resource.is_urgent) && (
        <div className="absolute top-2 left-2 z-10 flex gap-1">
          {resource.is_urgent && (
            <Badge className="bg-rose-600 text-white border-rose-700 text-[10px] font-bold shadow-sm" data-testid={`knowledge-urgent-${resource.resource_id}`}>
              URGENT
            </Badge>
          )}
          {isNew && (
            <Badge className="bg-emerald-500 text-white border-emerald-600 text-[10px] font-bold shadow-sm" data-testid={`knowledge-new-${resource.resource_id}`}>
              NEW
            </Badge>
          )}
        </div>
      )}
      {/* Cover is the primary click target — opens the resource. */}
      <div
        className="cursor-pointer"
        onClick={openLink}
        role="button"
        tabIndex={0}
        aria-label={`Open ${resource.title}`}
        data-testid={`knowledge-cover-${resource.resource_id}`}
      >
        <ResourceCover resource={resource} />
      </div>
      <CardContent className="p-4 space-y-2 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          {/* Title is also a click target — a native anchor so the
              browser shows the correct hover state + open-in-new-tab
              gesture. */}
          <a
            href={resource.url || '#'}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => { e.stopPropagation(); }}
            className="font-heading font-semibold text-sm leading-snug hover:underline"
            style={{ color: 'var(--kr-navy-900)' }}
            data-testid={`knowledge-title-${resource.resource_id}`}
          >
            {resource.title}
          </a>
          <button
            type="button"
            onClick={openLink}
            className="shrink-0 mt-0.5 text-slate-400 hover:text-slate-700"
            aria-label="Open resource"
            data-testid={`knowledge-open-icon-${resource.resource_id}`}
          >
            <ExternalLink className="w-4 h-4" />
          </button>
        </div>
        {resource.description && (
          // Full description — no line-clamp. Click does NOT propagate
          // so the agent can read/copy/highlight the text without the
          // card firing openLink.
          <p
            className="text-xs text-slate-600 leading-relaxed whitespace-pre-line break-words"
            onClick={(e) => e.stopPropagation()}
            data-testid={`knowledge-description-${resource.resource_id}`}
          >
            {resource.description}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          <Badge variant="outline" className={`text-[10px] ${meta.tone}`}>
            <meta.icon className="w-3 h-3 mr-1" />
            {meta.label}
          </Badge>
          {(resource.categories || []).slice(0, 3).map((c) => (
            <Badge key={c} variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-200">
              {c}
            </Badge>
          ))}
          {resource.categories?.length > 3 && (
            <span className="text-[10px] text-slate-400">+{resource.categories.length - 3}</span>
          )}
        </div>
        {(resource.attachments || []).length > 0 && (
          <div
            className="pt-2 mt-2 border-t border-slate-100 space-y-1"
            onClick={(e) => e.stopPropagation()}
            data-testid={`knowledge-attachments-${resource.resource_id}`}
          >
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
              Additional resources
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(resource.attachments || []).map((att, idx) => {
                const km = ATTACHMENT_KIND_META[att.kind] || ATTACHMENT_KIND_META.link;
                const KIcon = km.icon;
                return (
                  <a
                    key={`${resource.resource_id}-att-${idx}`}
                    href={att.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] hover:shadow-sm hover:brightness-95 transition ${km.tone}`}
                    data-testid={`knowledge-attachment-${resource.resource_id}-${idx}`}
                  >
                    <KIcon className="w-3 h-3" />
                    <span className="max-w-[160px] truncate">{att.title}</span>
                    <ExternalLink className="w-2.5 h-2.5 opacity-70" />
                  </a>
                );
              })}
            </div>
          </div>
        )}
        {isKR && (
          <div className="flex items-center gap-1.5 pt-2 border-t border-slate-100" onClick={(e) => e.stopPropagation()}>
            <Button
              size="sm" variant="ghost"
              className="h-7 text-xs text-slate-600 hover:text-slate-900"
              onClick={() => onEdit(resource)}
              data-testid={`knowledge-edit-${resource.resource_id}`}
            >
              <Edit3 className="w-3 h-3 mr-1" /> Edit
            </Button>
            <Button
              size="sm" variant="ghost"
              className="h-7 text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50"
              onClick={() => onDelete(resource)}
              data-testid={`knowledge-delete-${resource.resource_id}`}
            >
              <Trash2 className="w-3 h-3 mr-1" /> Delete
            </Button>
            {!resource.is_active && (
              <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200 ml-auto">
                Inactive
              </Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ResourceForm({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [categoryInput, setCategoryInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const isEdit = !!initial?.resource_id;

  useEffect(() => {
    if (open) {
      setForm(initial ? {
        title: initial.title || '',
        description: initial.description || '',
        url: initial.url || '',
        resource_type: initial.resource_type || 'video',
        categories: initial.categories || [],
        is_active: initial.is_active !== false,
        attachments: (initial.attachments || []).map((a) => ({
          title: a.title || '',
          url: a.url || '',
          kind: a.kind || 'link',
        })),
        notify_agents_on_publish: initial.notify_agents_on_publish !== false,
        is_urgent: !!initial.is_urgent,
      } : EMPTY_FORM);
      setCategoryInput('');
    }
  }, [open, initial]);

  const addCategory = () => {
    const v = categoryInput.trim();
    if (!v || form.categories.includes(v)) return;
    setForm((p) => ({ ...p, categories: [...p.categories, v] }));
    setCategoryInput('');
  };
  const removeCategory = (c) => setForm((p) => ({
    ...p, categories: p.categories.filter((x) => x !== c),
  }));

  // Attachments — supplementary links (docs, slides, PDFs) that
  // reinforce the main training video/document.
  const addAttachment = () => setForm((p) => ({
    ...p, attachments: [...p.attachments, { title: '', url: '', kind: 'link' }],
  }));
  const updateAttachment = (idx, patch) => setForm((p) => ({
    ...p,
    attachments: p.attachments.map((a, i) => (i === idx ? { ...a, ...patch } : a)),
  }));
  const removeAttachment = (idx) => setForm((p) => ({
    ...p, attachments: p.attachments.filter((_, i) => i !== idx),
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.url.trim()) {
      toast.error('Title and URL are required');
      return;
    }
    setSaving(true);
    try {
      // Normalize + drop half-filled attachments so we never persist
      // rows with an empty title or URL.
      const payload = {
        ...form,
        attachments: form.attachments
          .map((a) => ({
            title: (a.title || '').trim(),
            url: (a.url || '').trim(),
            kind: a.kind || 'link',
          }))
          .filter((a) => a.title && a.url),
      };
      let saved;
      if (isEdit) {
        const r = await knowledgeAPI.update(initial.resource_id, payload);
        saved = r.data;
      } else {
        const r = await knowledgeAPI.create(payload);
        saved = r.data;
      }
      toast.success(isEdit ? 'Resource updated' : 'Resource created');
      onSaved?.(saved);
      onOpenChange(false);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const handleCoverUpload = async (file) => {
    if (!isEdit) {
      toast.error('Guarda el recurso primero antes de subir un cover');
      return;
    }
    if (!file) return;
    setUploadingCover(true);
    try {
      await knowledgeAPI.uploadCover(initial.resource_id, file);
      toast.success('Cover subido');
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err, 'No se pudo subir el cover'));
    } finally {
      setUploadingCover(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="knowledge-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {isEdit ? 'Edit Resource' : 'New Resource'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Title *</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              placeholder="e.g. How to close Medicare Advantage sales"
              required
              data-testid="knowledge-form-title"
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="Short description of what the agent will learn…"
              data-testid="knowledge-form-description"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-2">
              <Label>URL *</Label>
              <Input
                type="url"
                value={form.url}
                onChange={(e) => setForm((p) => ({ ...p, url: e.target.value }))}
                placeholder="https://youtube.com/watch?v=... or https://drive.google.com/..."
                required
                data-testid="knowledge-form-url"
              />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select
                value={form.resource_type}
                onValueChange={(v) => setForm((p) => ({ ...p, resource_type: v }))}
              >
                <SelectTrigger data-testid="knowledge-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="video">Video</SelectItem>
                  <SelectItem value="document">Document</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Categories</Label>
            <div className="flex gap-2">
              <Input
                value={categoryInput}
                onChange={(e) => setCategoryInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); addCategory(); }
                }}
                placeholder="e.g. Sales, Compliance, Portal…"
                data-testid="knowledge-form-category-input"
              />
              <Button type="button" variant="outline" size="sm" onClick={addCategory}>
                Add
              </Button>
            </div>
            {form.categories.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1">
                {form.categories.map((c) => (
                  <Badge key={c} variant="outline" className="text-xs pl-2 pr-1 py-0.5 bg-slate-50">
                    {c}
                    <button
                      type="button"
                      onClick={() => removeCategory(c)}
                      className="ml-1 rounded hover:bg-slate-200 p-0.5"
                      aria-label={`Remove ${c}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Attachments — supplementary links displayed underneath the
              main training video/document on the resource card. */}
          <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-semibold">Additional resources</Label>
                <p className="text-xs text-slate-500">
                  Links to slides, PDFs or other documents that reinforce this
                  training. Shown below the resource card.
                </p>
              </div>
              <Button
                type="button" variant="outline" size="sm"
                onClick={addAttachment}
                data-testid="knowledge-form-add-attachment"
              >
                <Plus className="w-3 h-3 mr-1" /> Add link
              </Button>
            </div>
            {form.attachments.length === 0 ? (
              <p className="text-[11px] text-slate-400 italic">
                No linked resources yet. Tap &ldquo;Add link&rdquo; to add the first one.
              </p>
            ) : (
              <div className="space-y-2">
                {form.attachments.map((att, idx) => {
                  const KIcon = (ATTACHMENT_KIND_META[att.kind] || ATTACHMENT_KIND_META.link).icon;
                  return (
                    <div
                      key={idx}
                      className="grid grid-cols-12 gap-2 items-start bg-white border border-slate-200 rounded-lg p-2"
                      data-testid={`knowledge-form-attachment-${idx}`}
                    >
                      <div className="col-span-5">
                        <Input
                          value={att.title}
                          onChange={(e) => updateAttachment(idx, { title: e.target.value })}
                          placeholder="Title (e.g. Training slides)"
                          className="h-8 text-xs"
                          data-testid={`knowledge-form-attachment-title-${idx}`}
                        />
                      </div>
                      <div className="col-span-5">
                        <Input
                          value={att.url}
                          onChange={(e) => {
                            const newUrl = e.target.value;
                            const patch = { url: newUrl };
                            // Auto-guess kind when the user paste a URL
                            // that clearly matches a known type, but only
                            // if they haven't already picked one.
                            if (!att.kind || att.kind === 'link') {
                              patch.kind = guessAttachmentKind(newUrl);
                            }
                            updateAttachment(idx, patch);
                          }}
                          placeholder="https://drive.google.com/… or https://…"
                          className="h-8 text-xs"
                          data-testid={`knowledge-form-attachment-url-${idx}`}
                        />
                      </div>
                      <div className="col-span-1">
                        <Select
                          value={att.kind}
                          onValueChange={(v) => updateAttachment(idx, { kind: v })}
                        >
                          <SelectTrigger
                            className="h-8 text-xs px-2"
                            data-testid={`knowledge-form-attachment-kind-${idx}`}
                          >
                            <KIcon className="w-3.5 h-3.5" />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(ATTACHMENT_KIND_META).map(([k, m]) => (
                              <SelectItem key={k} value={k}>{m.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <Button
                          type="button" variant="ghost" size="icon"
                          className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                          onClick={() => removeAttachment(idx)}
                          data-testid={`knowledge-form-attachment-remove-${idx}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {isEdit && (
            <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Cover (optional)</Label>
                {initial?.cover_storage_key && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs text-rose-600"
                    onClick={async () => {
                      try {
                        await knowledgeAPI.deleteCover(initial.resource_id);
                        toast.success('Cover removed');
                        onSaved?.();
                      } catch (err) {
                        toast.error(getErrorMessage(err, 'Failed to remove cover'));
                      }
                    }}
                  >
                    Remove cover
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-xs text-sky-700 cursor-pointer hover:text-sky-900">
                  {uploadingCover ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  <span>Upload image (JPG, PNG or WebP)</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => handleCoverUpload(e.target.files?.[0])}
                    data-testid="knowledge-form-cover-upload"
                  />
                </label>
              </div>
              <p className="text-[11px] text-slate-500">
                Recommended: 16:9 ratio. If it&apos;s a YouTube video and no
                cover is uploaded, we use the thumbnail automatically.
              </p>
            </div>
          )}
          <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div>
              <Label className="text-sm font-semibold">Active</Label>
              <p className="text-xs text-slate-500">Inactive resources are hidden from agents.</p>
            </div>
            <Switch
              checked={form.is_active}
              onCheckedChange={(v) => setForm((p) => ({ ...p, is_active: !!v }))}
              data-testid="knowledge-form-active"
            />
          </div>
          {/* Notification controls — only meaningful when the resource
              is (or is about to be) published. */}
          <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div>
              <Label className="text-sm font-semibold">Notify agents on publish</Label>
              <p className="text-xs text-slate-500">
                Sends an email + in-app notification to every active agent when this resource goes live.
              </p>
            </div>
            <Switch
              checked={form.notify_agents_on_publish}
              onCheckedChange={(v) => setForm((p) => ({ ...p, notify_agents_on_publish: !!v }))}
              data-testid="knowledge-form-notify"
            />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-rose-200 bg-rose-50 p-3">
            <div>
              <Label className="text-sm font-semibold text-rose-900">Mark as urgent (adds SMS)</Label>
              <p className="text-xs text-rose-700">
                Also texts every active agent with a short link. Use sparingly — reserved for time-critical trainings.
              </p>
            </div>
            <Switch
              checked={form.is_urgent}
              onCheckedChange={(v) => setForm((p) => ({ ...p, is_urgent: !!v }))}
              data-testid="knowledge-form-urgent"
            />
          </div>
          {isEdit && initial?.is_active && (
            <div className="rounded-lg border border-sky-200 bg-sky-50 p-3">
              <Label className="text-sm font-semibold text-sky-900">Re-notify now</Label>
              <p className="text-xs text-sky-800 mb-2">
                Already published? Fire a fresh notification blast to every active agent — useful after a substantive update.
              </p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={saving}
                onClick={async () => {
                  try {
                    setSaving(true);
                    const r = await knowledgeAPI.update(initial.resource_id, {
                      renotify_now: true,
                      is_urgent: form.is_urgent,
                    });
                    toast.success('Notification blast dispatched to all active agents.');
                    onSaved?.(r.data);
                  } catch (err) {
                    toast.error(getErrorMessage(err, 'Failed to re-notify'));
                  } finally {
                    setSaving(false);
                  }
                }}
                data-testid="knowledge-form-renotify"
              >
                Re-notify all agents
              </Button>
            </div>
          )}
          <DialogFooter>
            <Button
              type="button" variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving} data-testid="knowledge-form-save">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isEdit ? 'Save changes' : 'Create resource'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function KnowledgePage() {
  const { user } = useAuth();
  const isKR = isKRRole(user);

  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [activeOnly, setActiveOnly] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        active_only: isKR ? activeOnly : true,
      };
      if (typeFilter !== 'all') params.resource_type = typeFilter;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (search.trim()) params.q = search.trim();
      const r = await knowledgeAPI.list(params);
      setResources(r.data?.items || []);
      setCategories(r.data?.categories || []);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load resources'));
    } finally {
      setLoading(false);
    }
  }, [typeFilter, categoryFilter, search, activeOnly, isKR]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (r) => { setEditing(r); setFormOpen(true); };
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await knowledgeAPI.remove(deleteTarget.resource_id);
      toast.success('Resource deleted');
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to delete'));
    }
  };

  const emptyState = useMemo(() => {
    if (loading) return null;
    if (resources.length > 0) return null;
    return (
      <div className="text-center py-16 border border-dashed border-slate-200 rounded-lg bg-slate-50">
        <BookOpen className="w-12 h-12 mx-auto text-slate-300 mb-3" />
        <h3 className="text-base font-semibold text-slate-700">
          No resources available
        </h3>
        <p className="text-sm text-slate-500 mt-1">
          {isKR
            ? 'Add your first video or document to start the library.'
            : 'No resources have been published yet. Check back soon.'}
        </p>
        {isKR && (
          <Button onClick={openNew} className="mt-4" data-testid="knowledge-empty-new-btn">
            <Plus className="w-4 h-4 mr-1" /> Add first resource
          </Button>
        )}
      </div>
    );
  }, [loading, resources.length, isKR]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader
        title="Knowledge"
        description="Library of KR-curated videos and documents."
        actions={
          isKR && (
            <Button onClick={openNew} data-testid="knowledge-new-btn">
              <Plus className="w-4 h-4 mr-1" /> New resource
            </Button>
          )
        }
      />

      {/* Filters */}
      <div className="mt-6 mb-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title or description…"
            className="pl-9"
            data-testid="knowledge-search"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-40" data-testid="knowledge-filter-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="video">Videos only</SelectItem>
            <SelectItem value="document">Documents only</SelectItem>
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-48" data-testid="knowledge-filter-category">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isKR && (
          <label className="flex items-center gap-2 text-xs text-slate-600 whitespace-nowrap">
            <Switch
              checked={!activeOnly}
              onCheckedChange={(v) => setActiveOnly(!v)}
              data-testid="knowledge-show-inactive"
            />
            Show inactive
          </label>
        )}
      </div>

      {/* Active filter chips */}
      {(categoryFilter !== 'all' || typeFilter !== 'all' || search) && (
        <div className="flex flex-wrap gap-2 mb-4">
          {typeFilter !== 'all' && (
            <Badge variant="outline" className="cursor-pointer" onClick={() => setTypeFilter('all')}>
              <Filter className="w-3 h-3 mr-1" /> {typeFilter} <X className="w-3 h-3 ml-1" />
            </Badge>
          )}
          {categoryFilter !== 'all' && (
            <Badge variant="outline" className="cursor-pointer" onClick={() => setCategoryFilter('all')}>
              <Filter className="w-3 h-3 mr-1" /> {categoryFilter} <X className="w-3 h-3 ml-1" />
            </Badge>
          )}
          {search && (
            <Badge variant="outline" className="cursor-pointer" onClick={() => setSearch('')}>
              <Search className="w-3 h-3 mr-1" /> &quot;{search}&quot; <X className="w-3 h-3 ml-1" />
            </Badge>
          )}
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <div className="aspect-video w-full bg-slate-200 animate-pulse" />
              <CardContent className="p-4 space-y-2">
                <div className="h-4 bg-slate-200 rounded animate-pulse w-3/4" />
                <div className="h-3 bg-slate-100 rounded animate-pulse w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : emptyState ? emptyState : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {resources.map((r) => (
            <ResourceCard
              key={r.resource_id}
              resource={r}
              isKR={isKR}
              onEdit={openEdit}
              onDelete={setDeleteTarget}
            />
          ))}
        </div>
      )}

      <ResourceForm
        open={formOpen}
        onOpenChange={setFormOpen}
        initial={editing}
        onSaved={load}
      />

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Delete resource</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Are you sure you want to delete <strong>{deleteTarget?.title}</strong>?
            Agents will no longer be able to see it.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button
              variant="destructive" onClick={confirmDelete}
              data-testid="knowledge-confirm-delete"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
